// Third-parties
import React, { PureComponent } from 'react';
import ReactTable, { ExpanderDefaults, SortingRule, SubComponentFunction } from 'react-table';
import selectTableHOC from 'react-table/lib/hoc/selectTable';
import classNames from 'classnames';
import get from 'lodash/get';
import 'react-table/react-table.css';
import { Waypoint } from 'react-waypoint';

// Components
import { Checkbox } from '../inputs';
import { LoadingSpinner } from '../common';

// Utils
import { formatMessage } from '@/utils/i18n';

const SelectTable = selectTableHOC(ReactTable);

type Props = {
  compact?: boolean;
  className?: string;
  data: any[] | null;
  columns: any[];
  canSelect: boolean;
  onSortingChange: (newSorted: SortingRule[]) => void;
  onRowClick?: (id: string) => void;
  selectedTitle?: string;
  selectedActions?: JSX.Element[];
  sortable: boolean;
  serverSideSorting?: boolean;
  selectOnRowClick?: boolean;
  singleSelection?: boolean;
  focusedId?: string;
  selected: any[];
  showLoadMore?: boolean;
  onLoadMore: () => void;
  onSelectItems: (ids: string[]) => void;
  onUnselectItems: (ids: string[]) => void;
  labelAccessor?: string;
  noDataText?: string | JSX.Element;
  scrollAncestor?: Window | HTMLElement | string;
  defaultSortDesc: boolean;
  subComponent?: SubComponentFunction;
  isUnselectable?: (row: any) => boolean;
  collapseOnDataChange?: boolean;
  expanderDefaults?: Partial<ExpanderDefaults>;
};

class DataTable extends PureComponent<Props> {
  private scrollContainer?: HTMLDivElement | null;

  static defaultProps = {
    canSelect: false,
    sortable: false,
    manual: false,
    serverSideSorting: false,
    defaultSortDesc: true,
    selected: [],
    onSortingChange: () => {},
    onSelectItems: () => {},
    onUnselectItems: () => {},
    onLoadMore: () => {},
    collapseOnDataChange: true,
  };

  selectTable: any;

  get selected(): any[] {
    return this.props.selected;
  }

  /**
   * Method to switch an item from the selected state
   */
  toggleSelection = (key: string) => {
    const { selected } = this.props;

    // Oddly enough, the single selection items have `select-` prepended to them
    const tmpKey = key.replace('select-', '');

    if (selected.some(i => i._id === tmpKey)) {
      this.props.onUnselectItems([tmpKey]);
    } else {
      this.props.onSelectItems([tmpKey]);
    }
  };

  allItemsAreSelected = (): boolean => {
    const { data, isUnselectable, selected } = this.props;

    if (!data) {
      return false;
    }
    if (isUnselectable && selected.length > 0) {
      const selectableRows = data.filter(portfolio => isUnselectable && !isUnselectable({ original: portfolio }));
      return selected.length === selectableRows.length;
    }

    return selected.length > 0 && !data.some((item: any) => !selected.some(i => i._id === item._id));
  };

  /**
   * Method that toggles all the selected items in the table
   */
  toggleAll = () => {
    const { selected, data } = this.props;

    if (!data) {
      return;
    }

    if (this.allItemsAreSelected()) {
      const itemsToUnselect = data.filter(d => selected.some(i => i._id === d._id));
      this.props.onUnselectItems(itemsToUnselect.map(i => i._id));
    } else {
      const itemsToSelect = data.filter(d => !selected.some(i => i._id === d._id));
      this.props.onSelectItems(itemsToSelect.map(i => i._id));
    }
  };

  /**
   * Returns true if an element is selected
   */
  isKeySelected = (key: string) => this.props.selected.some(i => i._id === key);

  /**
   * Check if checkbox is selected
   */
  getSelectInputComponent = (item: any) => {
    const isSelected = this.isKeySelected(item.row._id);
    const itemLabel = this.props.labelAccessor ? get(item.row._original, this.props.labelAccessor) : item.row._id;
    return <Checkbox selected={isSelected} aria-label={formatMessage('Common.Accessibility.SelectItem', { item: itemLabel })} />;
  };

  getSelectAllInputComponent = () => {
    const invert = this.props.selected.length > 0;
    const isSelected = this.allItemsAreSelected();

    return (
      <Checkbox
        invert={invert}
        selected={isSelected}
        onChange={this.toggleAll}
        aria-label={formatMessage(isSelected ? 'Common.Accessibility.DeselectAllRows' : 'Common.Accessibility.SelectAllRows')}
      />
    );
  };

  getCellProps = (state: any, rowInfo: any, column: any) => ({
    onClick: (e: any, handleOriginal: any) => {
      if (column.id === '_id' || !rowInfo.row._original || column.className.includes('action')) {
        return;
      }

      if (column.id === '_selector') {
        this.toggleSelection(rowInfo.row._original._id);
      } else if (this.props.onRowClick) {
        this.props.onRowClick(rowInfo.row._original._id);
      }

      if (handleOriginal) {
        handleOriginal();
      }
    },
  });

  getTableProps = () => ({
    className: 'd-flex flex-column',
  });

  getBodyProps = () => ({
    className: 'flex-grow-1',
  });

  getRowProps = (table: any, rowInfo: any) => {
    let props: any = {
      className: classNames({
        selected: this.selected.some(i => rowInfo.row._original && i._id === rowInfo.row._original._id),
        focused: rowInfo.row._original && this.props.focusedId && rowInfo.row._original._id === this.props.focusedId,
        clickable: this.props.onRowClick || this.props.selectOnRowClick,
        'unselectable-row': this.props.isUnselectable && this.props.isUnselectable(rowInfo),
      }),
    };

    if (this.props.selectOnRowClick && rowInfo && rowInfo.row) {
      props = {
        ...props,
        onClick: () => this.toggleSelection(rowInfo.original._id),
      };
    }

    return props;
  };

  getRowGroupProps = (table: any, rowInfo: any) => {
    const props: any = {
      className: classNames({
        'row-expanded': table.expanded[rowInfo.index],
      }),
    };

    return props;
  };

  getScrollableAncestor = () => {
    const { scrollAncestor } = this.props;

    if (scrollAncestor) {
      if (typeof scrollAncestor === 'string') {
        return document.querySelector(scrollAncestor);
      }

      return scrollAncestor;
    }

    return this.scrollContainer;
  };

  render() {
    const {
      className,
      data,
      columns,
      canSelect,
      selectedTitle,
      selectedActions,
      defaultSortDesc,
      showLoadMore,
      selected,
      labelAccessor,
      onLoadMore,
      noDataText,
      compact,
      subComponent,
    } = this.props;

    const selectionProps = {
      SelectInputComponent: this.getSelectInputComponent,
      SelectAllInputComponent: this.getSelectAllInputComponent,
      isSelected: this.isKeySelected,
    };

    const sortProps = {
      sortable: this.props.sortable,
      // Indicates that sorting and filtering is done server-side
      manual: this.props.sortable && this.props.serverSideSorting,
      onSortedChange: (newSorted: SortingRule[]) => this.props.onSortingChange(newSorted),
      defaultSortDesc,
    };

    const Table = canSelect ? SelectTable : ReactTable;
    const hasSelection = !!selected.length;

    const selectedLabels =
      labelAccessor && hasSelection
        ? selected.map((s, i) => (
            <div key={s._id} className="selected-label">
              <span data-private data-dd-privacy="mask">
                {get(s, labelAccessor)}
              </span>
              <i className="icon-ds-delete close-btn" onClick={() => this.props.onUnselectItems([s._id])} />
              {i < selected.length - 1 && <span>,</span>}
            </div>
          ))
        : null;

    const showSelectionHeader = hasSelection && (selectedTitle || (selectedActions && selectedActions.length) || selectedLabels);

    const classes = classNames('data-table d-flex flex-column', className, {
      compact,
      'has-selection': hasSelection,
      'no-selection-header': !showSelectionHeader,
      selectable: canSelect,
    });

    return (
      <div
        className={classes}
        ref={element => {
          this.scrollContainer = element;
        }}
      >
        {showSelectionHeader && (
          <div className="selection-header d-flex flex-column align-items-center">
            <div className="d-flex">
              <div className="title flex-grow-1">{selectedTitle}</div>

              {selectedActions}
            </div>

            <div>{selectedLabels}</div>
          </div>
        )}

        <Table
          ref={r => {
            this.selectTable = r;
          }}
          data={data || []}
          columns={columns}
          showPagination={false}
          resizable={false}
          minRows={0}
          pageSize={data ? data.length : undefined}
          {...selectionProps}
          {...sortProps}
          getTableProps={this.getTableProps}
          getTbodyProps={this.getBodyProps}
          getTrGroupProps={this.getRowGroupProps}
          getTrProps={this.getRowProps}
          getTdProps={this.getCellProps}
          noDataText={noDataText}
          SubComponent={subComponent}
          collapseOnDataChange={this.props.collapseOnDataChange}
          expanderDefaults={this.props.expanderDefaults}
        />

        {showLoadMore && data && data.length > 0 && (
          <div className="load-more d-flex justify-content-center ">
            <Waypoint onEnter={onLoadMore} scrollableAncestor={this.getScrollableAncestor()} />
            <LoadingSpinner />
          </div>
        )}
      </div>
    );
  }
}

export default DataTable;
